import React, {ReactNode, useState} from 'react';

import {cn} from "../../lib/utils.ts";
import RelativeLoadingFrame from "../RelativeLoadingFrame.tsx";

interface TabProps {
    label?: string;
    icon?: React.ComponentType;
    children: ReactNode;
    slug?: string;
    hidden?: boolean;
}

interface TabsProps {
    children: ReactNode;
    isLoading?: boolean;
    defaultActiveTab?: number;
    activeSlug?: string;
    onTabChange?: (slug: string) => void;
    notice?: ReactNode;
}

export function Tabs({children, isLoading, defaultActiveTab = 0, activeSlug, onTabChange, notice}: TabsProps): JSX.Element {
    const [activeIndex, setActiveIndex] = useState(defaultActiveTab);

    const tabs = React.Children.toArray(children).filter(React.isValidElement) as React.ReactElement<TabProps>[];
    const selectedBySlug = activeSlug !== undefined;

    const firstVisibleIndex = Math.max(0, tabs.findIndex((tab) => !tab.props.hidden));

    let activeTabIndex: number;
    if (selectedBySlug) {
        const matchedIndex = tabs.findIndex((tab) => tab.props.slug === activeSlug);
        activeTabIndex = matchedIndex >= 0 ? matchedIndex : firstVisibleIndex;
    } else {
        activeTabIndex = activeIndex;
    }

    function selectTab(tab: React.ReactElement<TabProps>, index: number) {
        if (!selectedBySlug) {
            setActiveIndex(index);
            return;
        }
        if (tab.props.slug) {
            onTabChange?.(tab.props.slug);
        }
    }

    return (
        <div>
            <div className="flex flex-nowrap gap-x-4 overflow-x-auto border-b border-gray-200 pb-3">
                {tabs.map((tab, index) => {
                    if (tab.props.hidden) {
                        return null;
                    }
                    const isActive = index === activeTabIndex;
                    return (
                        <button key={tab.props.slug ?? index} onClick={() => selectTab(tab, index)} className={
                            (isActive ? 'bg-gray-50 text-primary' : 'text-gray-500 hover:text-gray-700') + ' group flex shrink-0 gap-x-3 whitespace-nowrap rounded-md py-2 pl-2 pr-3 text-sm/6 font-semibold'}>
                            {tab.props.icon && <tab.props.icon
                                //@ts-ignore
                                className={cn(
                                    isActive ? 'text-primary' : 'text-gray-400 group-hover:text-primary',
                                    'size-5 shrink-0',
                                )}
                                aria-hidden="true"
                            />} {tab.props.label || `Tab ${index + 1}`}

                        </button>
                    );
                })}
            </div>

            {notice}

            <RelativeLoadingFrame isLoading={isLoading}><div className="mt-4">
                {tabs.map((tab, index) =>
                    index === activeTabIndex ? tab : null
                )}
            </div>
            </RelativeLoadingFrame>
        </div>
    );
}


export function Tab({label, icon, children}: TabProps): JSX.Element {
    return <div className="relative">{children}</div>;
}
